import { Headline2 } from '@/app/components/Typography'
import { LinkedInIconInverted } from '@/app/icons/LinkedInIconInverted/LinkedInIconInverted'
import { ICourse, IInstructor } from '@/types/course'
import { useTranslations } from 'next-intl'
import Link from 'next/link'
import { useEffect, useState } from 'react'
import { ImageWithFallback } from '../../shared/components/image-with-fallback'

type CourseInstructorsProps = {
  courseInfo: Required<Pick<ICourse, 'instructors'>>
} & React.HTMLAttributes<HTMLDivElement>

export function CourseInstructors({
  courseInfo,
  ...props
}: CourseInstructorsProps) {
  const t = useTranslations('CoursePage')
  const { instructors = [] } = courseInfo
  const [isLoading, setLoading] = useState(
    !!Array.isArray(instructors) && !!instructors.length
  )
  const [loadedInstructors, setLoadedInstructors] = useState<IInstructor[]>([])

  const validInstructors = (instructors ?? []).filter(
    (instructor) =>
      instructor && instructor.name && instructor.bio && instructor.avatarUrl
  )

  useEffect(() => {
    // TODO: optimize this not to request each image two times: one the original, other for the next optimized one
    if (!isLoading) {
      return
    }

    ;(async () => {
      const instructorImagesPromises: Promise<IInstructor>[] =
        validInstructors.map((instructor) => {
          const { avatarUrl } = instructor
          return new Promise((resolve, reject) => {
            const image = new Image()
            image.onload = () => {
              resolve(instructor)
            }
            image.onerror = () => reject()
            image.setAttribute('src', avatarUrl)
          })
        })

      const result = await Promise.allSettled(instructorImagesPromises)
      const fulfilledInstructors = Array.from(result)
        .filter((r) => r.status === 'fulfilled')
        .map((r) => ({ ...r.value, status: r.status }))

      const [loaded, failed] = validInstructors.reduce(
        ([loaded, failed], instructor) => {
          const found = fulfilledInstructors.find(
            (i) => i.avatarUrl === instructor.avatarUrl
          )
          return found
            ? [[...loaded, found], failed]
            : [loaded, [...failed, instructor]]
        },
        [[], []] as [IInstructor[], IInstructor[]]
      )

      const x = [...loaded, ...failed]

      setLoadedInstructors(x)
      setLoading(false)
    })()
  }, [validInstructors, isLoading])

  if (!Array.isArray(instructors) || !validInstructors.length) {
    return null
  }

  return (
    <div
      {...props}
      className={`flex flex-col gap-12 py-8 md:py-20 ${props.className}`}
    >
      <Headline2 className="text-center">
        {t('meet_your_instructor', {
          count: instructors.length,
        })}
      </Headline2>
      <ul className="flex flex-col gap-4">
        {(isLoading ? validInstructors : loadedInstructors).map(
          ({ name, role, avatarUrl, linkedIn, company, bio }, index) => (
            <li className="flex flex-col gap-6 md:flex-row" key={index}>
              <div className="mx-auto aspect-square h-16 w-16 shrink-0 self-start overflow-hidden rounded-2xl md:mx-0 md:h-[5.625rem] md:w-[5.625rem]">
                {isLoading ? (
                  <div className="h-full w-full animate-pulse bg-mydra-gray blur" />
                ) : (
                  <ImageWithFallback
                    src={avatarUrl ?? ''}
                    alt={role ?? t('instructor_avatar')}
                    width={90}
                    height={90}
                    className={`aspect-square h-full w-full self-center rounded-2xl`}
                    fallbackSrc="/images/profile-picture.png"
                    quality={90}
                  />
                )}
              </div>

              <div className={`flex flex-col gap-6 ${isLoading ? 'blur' : ''}`}>
                <div className="text-sm text-mydra-black md:text-base">
                  <p className="font-semibold md:text-lg">{name}</p>
                  {(role || company) && (
                    <p className="">
                      {[role, company].filter(Boolean).join(', ')}
                    </p>
                  )}
                  {bio && <p>{bio}</p>}
                </div>
                {linkedIn && (
                  <Link
                    className="mt-auto self-start rounded-full border border-black p-2"
                    target="_blank"
                    href={linkedIn}
                  >
                    <LinkedInIconInverted className="mx-auto h-3 w-3" />
                  </Link>
                )}
              </div>
            </li>
          )
        )}
      </ul>
    </div>
  )
}
